import React from 'react';
import {Header} from 'semantic-ui-react';

import {GetArgosJobOptions, GetArgosJob} from '@graphql/argosJob/query';
import {CreateArgosJob, DeleteArgosJob, UpdateArgosJob} from '@graphql/argosJob/mutation';
import {ARGOS_JOB} from '@constants/resources';
import Form from '@appComponents/ReduxFormControls';
import TimeField from '@modules/events/forms/ArgosJob/TimeField';
import * as MESSAGES from '@constants/messages';

import validate from './validator';

export const TIME_TYPE_MANUAL = 'manual';
export const TIME_TYPE_MARGIN = 'margin';
export const TIME_TYPE_FIXED = 'fixed';

export default function ArgosJobModel() {
    return {
        title: () => ('Schedule new Argos job'),
        entityLabel: 'Argos Job',
        entityDataMapKey: 'argosJob',
        formName: 'argosJob',
        modalClassName: 'argosJobModal',
        resources: ARGOS_JOB,
        showChangeLog: true,
        validate: validate,
        label: 'Argos Job',
        messages: {
            boxName: {
                delete: 'argosJobMessage',
                error: 'argosJobMessageError',
                success: 'argosJobMessage',
            },
            text: {
                ...MESSAGES,
                FORM_ERROR: () => MESSAGES.FORM_ERROR('Argos job'),
            },
        },
        mutation: {
            createEntity: CreateArgosJob,
            updateEntity: UpdateArgosJob,
            deleteEntity: DeleteArgosJob,
        },
        query: {
            getEntity: GetArgosJob,
            getOptions: GetArgosJobOptions,
        },
        fields: {
            id: {
                dataMapKey: 'argosJob.id',
                props: {
                    component: Form.IdField,
                    label: '',
                    name: 'id',
                    hidden: true,
                },
            },
            header_settings: {
                props: {
                    component: headerHelper,
                    name: 'header_settings',
                    defaultValue: {
                        name: 'Settings',
                    },
                },
            },
            job_definition: {
                optionsKey: 'argosJobDefinitions',
                dataMapKey: 'argosJob.argos_job_definition.id',
                props: {
                    component: Form.SemanticSelect,
                    label: 'Job definition',
                    required: true,
                    name: 'job_definition',
                },
            },
            region: {
                optionsKey: 'argosJobRegions',
                dataMapKey: 'argosJob.argos_job_region.id',
                props: {
                    component: Form.SemanticSelect,
                    label: 'Region',
                    required: true,
                    name: 'region',
                },
            },
            entry_point: {
                optionsKey: 'argosSrtOriginEntryPoints',
                dataMapKey: 'argosJob.srt_origin_entry_point.id',
                props: {
                    component: Form.SemanticSelect,
                    label: 'Entry point',
                    required: true,
                    name: 'entry_point',
                },
            },
            output_tunnel_group: [
                {
                    tunnel_clients: {
                        optionsKey: 'clientsWithThirdPartyClientTunnels',
                        dataMapKey: 'argosJob.third_party_client_tunnel.client_product_third_party.client_product.client.id',
                        props: {
                            component: Form.SemanticSelect,
                            label: 'Output tunnel',
                            required: true,
                            name: 'tunnel_clients',
                        },
                    },
                },
                {
                    third_party_client_tunnel: {
                        dataMapKey: 'argosJob.third_party_client_tunnel.id',
                        props: {
                            component: Form.SemanticSelect,
                            label: '',
                            required: true,
                            name: 'third_party_client_tunnel',
                            hidden: true,
                        },
                    },
                },
            ],
            argos_description: {
                dataMapKey: 'argosJob.description',
                props: {
                    component: Form.SemanticInput,
                    label: 'Description',
                    name: 'argos_description',
                    type: 'input',
                },
            },
            header_start_time: {
                props: {
                    component: headerHelper,
                    name: 'header_start_time',
                    defaultValue: {name: 'Start time'},
                },
            },
            start_time: {
                props: {
                    label: '',
                    component: TimeField,
                    name: 'start_time_type',
                    radioOptions: [
                        {
                            id: 'start_time_manual',
                            value: TIME_TYPE_MANUAL,
                            label: 'Trigger manually (no start time)',
                        },
                        {
                            id: 'start_margin',
                            value: TIME_TYPE_MARGIN,
                            label: '',
                            name: 'start_time_margin',
                            type: 'number',
                            inputLabel: 'minutes before the event start time',
                            inputProps: {min: '0'},
                        },
                        {
                            id: 'start_fixed',
                            value: TIME_TYPE_FIXED,
                            label: 'Fixed time:',
                            name: 'start_time_fixed',
                            type: 'date',
                            configuration: {
                                showTimeSelect: true,
                                timeFormat: 'HH:mm',
                                timeIntervals: 5,
                            },
                        },
                    ],
                },
            },
            header_end_time: {
                props: {
                    component: headerHelper,
                    name: 'header_end_time',
                    defaultValue: {name: 'End time'},
                },
            },
            end_time: {
                props: {
                    label: '',
                    component: TimeField,
                    name: 'end_time_type',
                    radioOptions: [
                        {
                            id: 'end_manual',
                            value: TIME_TYPE_MANUAL,
                            label: 'Trigger manually (no end time)',
                        },
                        {
                            id: 'end_margin',
                            value: TIME_TYPE_MARGIN,
                            label: '',
                            name: 'end_time_margin',
                            type: 'number',
                            hidden: true,
                            inputProps: {min: '0'},
                        },
                        {
                            id: 'end_fixed',
                            value: TIME_TYPE_FIXED,
                            label: 'Fixed time:',
                            name: 'end_time_fixed',
                            type: 'date',
                            configuration: {
                                showTimeSelect: true,
                                timeFormat: 'HH:mm',
                                timeIntervals: 5,
                            },
                        },
                    ],
                },
            },
        },
    };
}

export const headerHelper = (props) => (
    <Header dividing className='argosJobFormHeader'>
        {props.defaultValue?.name}
    </Header>
);
